import { Component, OnDestroy, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { Erzeugungsanlage } from '../../domain/model/erzeugungsanlage';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { PreferencesService } from '../../service/common/preferences/preferences.service';
import { ErzeugungsanlageService, Page } from '../../service/erzeugungsanlage/erzeugungsanlage.service';
import { debounceTime, filter, map, shareReplay, skip, startWith, switchMap, take, takeWhile } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { RoutingService } from '../../service/common/routing/routing.service';
import { combineLatest, distinctUntilChanged, merge, Observable, ReplaySubject, Subject } from 'rxjs';
import { ErzeugungsanlageFilter } from '../../service/erzeugungsanlage/erzeugungsanlage-filter';
import { ErzeugungsanlagenRefreshService } from '../../service/erzeugungsanlage/erzeugungsanlagen-refresh.service';
import { TnbUser } from '../../service/auth/tnb-user';
import { Pageable } from '../../common/paging/pageable';
import { TeilgebietSelectionService } from '../../service/common/teilgebiet/teilgebiet-selection.service';
import { Netzbetreiber } from '../../domain/model/netzbetreiber';
import moment from 'moment';
import { NetzbetreiberService } from '../../service/netzbetreiber/netzbetreiber.service';
import { NotificationService } from '../../service/common/notification/notification.service';
import { AuthenticationService } from '../../service/auth/authentication.service';
import { DatePipe } from '@angular/common';
import { distinctUntilFilterChanged } from '../../common/util/helper';

@Component({
    selector: 'tnb-erzeugungsanlage-list',
    templateUrl: './erzeugungsanlage-list.component.html',
    styleUrls: [
        './erzeugungsanlage-list.component.scss',
    ],
    standalone: false,
})
export class ErzeugungsanlageListComponent implements OnDestroy, OnInit {
    private preferencesService = inject(PreferencesService);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private routeService = inject(RoutingService);
    private erzeugungsanlageService = inject(ErzeugungsanlageService);
    private erzeugungsanlagenRefreshService = inject(ErzeugungsanlagenRefreshService);
    private netzbetreiberService = inject(NetzbetreiberService);
    private teilgebietSelectionService = inject(TeilgebietSelectionService);
    private notificationService = inject(NotificationService);
    private changeDetectorRef = inject(ChangeDetectorRef);
    authService = inject(AuthenticationService);

    public readonly VIEW_GRID = 'grid';
    public readonly VIEW_TABLE = 'table';
    public readonly PAGE_SIZE = 50;

    public viewMode = this.VIEW_GRID;

    public page$: Observable<Pageable>;
    public filter$: Observable<ErzeugungsanlageFilter>;
    public erzeugungsanlagenPage$: Observable<Page<Erzeugungsanlage>>;

    public readonly profile: TnbUser;

    private componentDestroyed = false;

    private pageFromUserInteraction$: Observable<Pageable>;
    private pageEventFromUserInteraction$ = new ReplaySubject<PageEvent>(1);
    private sortFromUserInteraction$ = new ReplaySubject<Sort>(1);
    private filterFromUserInteraction$ = new Subject<ErzeugungsanlageFilter>();

    public lastChanged: Date;
    public recentlyConfirmed = false;

    constructor() {
        this.profile = this.route.snapshot.data.profile;

        this.setupInitialViewModeFromUserSettings();
        this.setupFilterObservable();
        this.setupPageObservable();
        this.setupErzeugungsanlageObservable();
    }

    ngOnInit(): void {
        if (this.profile.netzbetreiberType === 'STADTWERK' && this.authService.isNvnb(this.profile)) {
            this.netzbetreiberService.getNetzbetreiber(this.teilgebietSelectionService.getSelectedStadtwerkeId())
                .pipe(take(1))
                .subscribe(nb => {
                    this.lastChanged = new Date(nb.lastChanged);
                    this.recentlyConfirmed = ErzeugungsanlageListComponent.sameDay(this.lastChanged, new Date());
                    this.changeDetectorRef.markForCheck();
                });
        }
    }

    ngOnDestroy(): void {
        this.componentDestroyed = true;
    }

    private setupPageObservable() {
        this.pageFromUserInteraction$ = combineLatest([
            this.sortFromUserInteraction$,
            this.pageEventFromUserInteraction$,
        ])
            .pipe(
                map(params => {
                    const pageable = new Pageable();

                    const sort = params[0];
                    const page = params[1];

                    pageable.sort = sort?.active || '';
                    pageable.direction = sort?.direction || '';
                    pageable.page = page?.pageIndex || 0;

                    return pageable;
                }),
                shareReplay(1),
            );

        this.route.queryParams
            .pipe(
                takeWhile(() => !this.componentDestroyed),
            )
            .subscribe(params => {
                this.sortFromUserInteraction$.next({
                    active: params['sort'],
                    direction: params['direction'],
                });
                this.pageEventFromUserInteraction$.next({
                    pageIndex: +params['page'],
                } as PageEvent);
            });

        this.page$ = this.pageFromUserInteraction$
            .pipe(
                distinctUntilFilterChanged(),
                shareReplay(1),
            );
    }

    private setupFilterObservable() {
        const filterFromQueryParam$ = this.route.queryParams
            .pipe(
                map((params: any) => {
                    const eaFilter = new ErzeugungsanlageFilter();
                    if (params) {
                        eaFilter.westnetzId = params['westnetzId'];
                        eaFilter.anlagentype = params['anlagentype'];
                        if (params['installierteLeistungMin']) {
                            const min = Number(params['installierteLeistungMin']);
                            eaFilter.installierteLeistungMin = isNaN(min) ? undefined : min;
                        }
                        if (params['installierteLeistungMax']) {
                            const max = Number(params['installierteLeistungMax']);
                            eaFilter.installierteLeistungMax = isNaN(max) ? undefined : max;
                        }
                        eaFilter.ortsnamen = params['ortsnamen'];
                        eaFilter.stationsnamen = params['stationsnamen'];
                        eaFilter.netzbetreiberName = params['netzbetreiberName'];
                        eaFilter.netzbetreiberTeilgebiet = params['netzbetreiberTeilgebiet'];
                        eaFilter.leitstellenRegion = params['leitstellenRegion'];
                        eaFilter.status = params['status'] ? JSON.parse(params['status']) : undefined;
                    }

                    if (this.profile.netzbetreiberType !== 'WESTNETZ') {
                        eaFilter.stadtwerkeId = this.teilgebietSelectionService.getSelectedStadtwerkeId();
                    }
                    return eaFilter;
                }),
            );

        this.filter$ = merge(
            this.filterFromUserInteraction$,
            filterFromQueryParam$,
        ).pipe(
            distinctUntilFilterChanged(),
            shareReplay(1),
        );
    }

    private setupErzeugungsanlageObservable() {
        // Goto first page, when filter changes
        this.filterFromUserInteraction$
            .pipe(
                skip(1),
                takeWhile(() => !this.componentDestroyed),
            )
            .subscribe(_filter => {
                const page = new PageEvent();
                page.pageIndex = 0;
                this.pageEventFromUserInteraction$.next(page);
            });

        combineLatest([
            this.pageFromUserInteraction$,
            this.filterFromUserInteraction$,
        ])
            .pipe(
                filter(() => {
                    const currentParams = this.route.snapshot.queryParams;
                    return !currentParams['erzeugungsanlage'] && !currentParams['createnew'];
                }),
                map(param => {
                    const pageable = param[0];
                    const eaFilter = param[1];
                    return this.routeService.erzeugungsanlageTable(pageable, eaFilter);
                }),
                distinctUntilChanged(),
                takeWhile(() => !this.componentDestroyed),
            )
            .subscribe(url => {
                this.router.navigateByUrl(url);
            });

        this.erzeugungsanlagenPage$ = combineLatest([
            this.page$,
            this.filter$,
            this.erzeugungsanlagenRefreshService.refreshRequested$.pipe(startWith(null)),
        ])
            .pipe(
                debounceTime(0),
                takeWhile(() => !this.componentDestroyed),
                switchMap(param => this.erzeugungsanlageService.getErzeugungsanlagen(param[0], param[1])),
            );
    }

    private setupInitialViewModeFromUserSettings() {
        this.viewMode = this.preferencesService.getPreferredErzeugungsanlagenView(this.VIEW_GRID);
    }

    toggleViewMode(event: MatButtonToggleChange) {
        this.preferencesService.setPreferredErzeugungsanlagenView(event.source.value);
    }

    filterChanged(eaFilter: ErzeugungsanlageFilter) {
        this.filterFromUserInteraction$.next(eaFilter);
    }

    pageChange($event: PageEvent) {
        this.pageEventFromUserInteraction$.next($event);
    }

    sortChange($event: Sort) {
        this.sortFromUserInteraction$.next($event);
    }

    public static sameDay(d1: Date, d2: Date): boolean {
        return d1.getFullYear() === d2.getFullYear() &&
            d1.getMonth() === d2.getMonth() &&
            d1.getDate() === d2.getDate();
    }

    public confirmUpToDateness(): void {
        this.netzbetreiberService.confirmUpToDateness(this.teilgebietSelectionService.getSelectedStadtwerkeId()).subscribe(
            (netzbetreiber: Netzbetreiber) => {
                this.notificationService.success('Die Aktualität Ihrer Daten wurde bestätigt');
                this.lastChanged = new Date(netzbetreiber.lastChanged);
                this.recentlyConfirmed = true;
                this.changeDetectorRef.markForCheck();
            },
        );
    }

    islastChangedNotUptoDate(): boolean {
        return moment(this.lastChanged).isAfter(moment().subtract(30, 'days'));
    }

    get lastChangedDateTooltipText(): string {
        return 'Zuletzt bestätigt am: ' + new DatePipe('de-de').transform(this.lastChanged,'dd.MM.yyyy');
    }
}
